"use client"

import { useState } from "react"
import Image from "next/image"
import { ChevronDown, ChevronUp, Play, ShoppingCart, Gift, Clapperboard } from "lucide-react"
import type { TMDBWatchProviderResult, TMDBVideo } from "@/lib/tmdb"
import { getProviderLogoUrl } from "@/lib/tmdb"

// Deduplicate providers that are variants of the same service (e.g. Netflix, Netflix basic with Ads)
function deduplicateProviders(
  providers: { logo_path: string; provider_name: string; provider_id: number; display_priority: number }[]
) {
  const MULTI_WORD_PREFIXES = ["apple tv", "amazon prime", "amazon video", "google play", "canal+", "france tv"]

  function getProviderKey(name: string): string {
    const lower = name.toLowerCase().trim()
    for (const prefix of MULTI_WORD_PREFIXES) {
      if (lower.startsWith(prefix)) return prefix
    }
    return lower.split(/[\s]/)[0]
  }

  const seen = new Map<string, typeof providers[number]>()
  for (const p of providers) {
    const key = getProviderKey(p.provider_name)
    if (!seen.has(key)) {
      seen.set(key, p)
    }
  }
  return Array.from(seen.values())
}

interface WatchProvidersProps {
  providers: TMDBWatchProviderResult | null
  trailer: TMDBVideo | null
  inTheaters?: boolean
  className?: string
}

export function WatchProviders({ providers, trailer, inTheaters = false, className = "" }: WatchProvidersProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  // Collect and deduplicate providers by category
  const streamingProviders = deduplicateProviders(providers?.flatrate || [])
  const freeProviders = deduplicateProviders(providers?.free || [])
  // Rent and Buy almost always list the same stores — merge into a single
  // "Location / achat" group to avoid showing identical logos twice.
  const rentBuyProviders = deduplicateProviders([
    ...(providers?.rent || []),
    ...(providers?.buy || []),
  ])

  // Preview: prefer streaming/free, fall back to rent/buy
  const primaryPreview = deduplicateProviders([...streamingProviders, ...freeProviders])
  const previewProviders = primaryPreview.length > 0
    ? primaryPreview.slice(0, 3)
    : rentBuyProviders.slice(0, 3)

  const hasProviders = streamingProviders.length > 0 ||
    freeProviders.length > 0 ||
    rentBuyProviders.length > 0

  if (!hasProviders && !trailer && !inTheaters) {
    return null
  }

  return (
    <div className={`${className}`}>
      {/* Compact row */}
      <div className="flex flex-wrap items-center gap-3">
        {/* In-theaters badge */}
        {inTheaters && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-400">
            <Clapperboard className="h-3.5 w-3.5" />
            Au cinéma
          </span>
        )}

        {/* Trailer Button — filled coral primary (matches the dashboard bar) */}
        {trailer && (
          <a
            href={`https://www.youtube.com/watch?v=${trailer.key}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90"
            style={{
              background: "var(--color-accent)",
              boxShadow: "0 8px 18px -8px rgba(219,98,66,.7)",
            }}
          >
            <Play className="h-4 w-4 fill-current" />
            Bande-annonce
          </a>
        )}

        {/* Streaming Providers Preview */}
        {previewProviders.length > 0 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <span className="text-xs text-gray-400">
              {primaryPreview.length > 0 ? "Disponible sur" : "En location / achat"}
            </span>
            <div className="flex -space-x-1">
              {previewProviders.map((provider) => (
                <div
                  key={provider.provider_id}
                  className="w-8 h-8 rounded-lg overflow-hidden shadow-sm"
                  title={provider.provider_name}
                >
                  {provider.logo_path ? (
                    <Image
                      src={getProviderLogoUrl(provider.logo_path, "w92")}
                      alt={provider.provider_name}
                      width={32}
                      height={32}
                      className="object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-violet-500 to-indigo-600 text-white text-xs font-bold">
                      {provider.provider_name.charAt(0)}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {hasProviders && (
              <span className="flex items-center text-xs text-gray-400">
                {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </span>
            )}
          </button>
        )}
      </div>

      {/* Expanded Section */}
      {isExpanded && hasProviders && (
        <div className="mt-4 p-4 bg-white/5 rounded-xl space-y-4 animate-in slide-in-from-top-2 duration-200">
          {/* Streaming (Subscription) */}
          {streamingProviders.length > 0 && (
            <ProviderRow
              title="Abonnement"
              icon={<Play className="h-3.5 w-3.5" />}
              providers={streamingProviders}
              color="text-green-400"
            />
          )}

          {/* Free */}
          {freeProviders.length > 0 && (
            <ProviderRow
              title="Gratuit"
              icon={<Gift className="h-3.5 w-3.5" />}
              providers={freeProviders}
              color="text-blue-400"
            />
          )}

          {/* Rent / Buy (merged — same stores usually offer both) */}
          {rentBuyProviders.length > 0 && (
            <ProviderRow
              title="Location / achat"
              icon={<ShoppingCart className="h-3.5 w-3.5" />}
              providers={rentBuyProviders}
              color="text-amber-400"
            />
          )}

        </div>
      )}
    </div>
  )
}

interface ProviderRowProps {
  title: string
  icon: React.ReactNode
  providers: { logo_path: string; provider_name: string; provider_id: number }[]
  color: string
}

function ProviderRow({ title, icon, providers, color }: ProviderRowProps) {
  return (
    <div className="flex items-center gap-3">
      <div className={`flex items-center gap-1.5 text-xs font-medium ${color} min-w-[90px]`}>
        {icon}
        {title}
      </div>
      <div className="flex flex-wrap gap-2">
        {providers.map((provider) => (
          <div
            key={provider.provider_id}
            className="group relative"
          >
            <div className="w-10 h-10 rounded-lg overflow-hidden shadow-sm transition-transform group-hover:scale-110">
              {provider.logo_path ? (
                <Image
                  src={getProviderLogoUrl(provider.logo_path, "w92")}
                  alt={provider.provider_name}
                  width={40}
                  height={40}
                  className="object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-violet-500 to-indigo-600 text-white text-sm font-bold">
                  {provider.provider_name.charAt(0)}
                </div>
              )}
            </div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
              {provider.provider_name}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// Legacy export for compatibility
export const WatchProvidersCompact = WatchProviders
